// @ts-check
/**
 * Domain URL resolution for Terra Nova MFEs.
 *
 * **Runtime classification**
 * - **Build-time / Node (no `window`)**: Uses `process.env` (`CURRENT_ENV`, `NAMESPACE`, `SUBDOMAIN`, `LOB`).
 *   Used by webpack/federation plugins and any import where `window` is undefined.
 * - **SSR / tests (`isServer === true`)**: Always uses `process.env` (and kube-style URLs when applicable).
 * - **Browser (`isServer === false` and `window` exists)**: Uses runtime bootstrap for env/branch
 *   ({@link getClientRuntimeCurrentEnv}, hostname-derived Platypus branch) so promoted images do not
 *   stick to build-time `CURRENT_ENV`; falls back to `process.env` when globals are unset (Jest, early load).
 *
 * Note: This file also runs at build-time in Node.js; `process.env` remains the source of truth there.
 */

/**
 * Get Line of Business from environment variable
 * This is a simplified version for domain generation that only reads from env vars
 * @param {string} defaultLOB - Default LOB if not set
 * @returns {string}
 */
const getLOB = (defaultLOB = 'pplus') => {
    return (process.env.LOB || defaultLOB).toLowerCase();
};

/**
 * Get the base domain for a given Line of Business
 * @param {string} lob - Line of business ('pplus' or 'ptv')
 * @returns {string}
 */
const getDomainByLOB = (lob) => {
    return lob === 'ptv' ? 'pluto.tv' : 'paramountplus.com';
};

const getCanonicalAppName = (name, lob) =>
    name === 'user-profiles' ? (lob === 'ptv' ? 'ptv-appshell' : 'app-shell') : name;

// MFE Paths for local Development.
// Set NEXT_PUBLIC_LOCAL_HTTPS=true (and LOCAL_HTTPS=true for server/build-time compatibility)
// to switch all remotes to https.
// Inline check, not the isLocalHttps() helper: many app tests partially mock
// getCurrentEnv, and a module-scope helper call breaks every suite whose import
// graph reaches this file.
const localHttpsEnabled =
    (process.env.NEXT_PUBLIC_LOCAL_HTTPS || process.env.LOCAL_HTTPS) === 'true';
const localProtocol = localHttpsEnabled ? 'https' : 'http';
/** @const */
export const mfePaths = {
    // TURBO GEN REWRITES
    'app-shell': `${localProtocol}://localhost:3000`,
    'user-profiles': `${localProtocol}://localhost:3001`,
    signup: `${localProtocol}://localhost:3003`,
    'chromecast-receiver': `${localProtocol}://localhost:3004`,
    'chromecast-sender': `${localProtocol}://localhost:3005`,
    hubs: `${localProtocol}://localhost:3006`,
    account: `${localProtocol}://localhost:3008`,
    video: `${localProtocol}://localhost:3009`,
    'ptv-appshell': `${localProtocol}://localhost:3010`,
};
/** @typedef { keyof typeof mfePaths } MfeNames */

// Server-side SSR/proxy calls in local HTTPS dev use these HTTP proxy ports (HTTPS port + 1000).
// undici (Node.js native fetch used by graphql-request 5.x and Next.js) ignores
// NODE_TLS_REJECT_UNAUTHORIZED, so server-side calls go through a plain HTTP proxy
// (scripts/multi-http-proxy.mjs) that handles the HTTPS hop with rejectUnauthorized: false.
/** @const */
const mfeHttpPaths = {
    'app-shell': 'http://localhost:4000',
    signup: 'http://localhost:4003',
    'chromecast-receiver': 'http://localhost:4004',
    'chromecast-sender': 'http://localhost:4005',
    hubs: 'http://localhost:4006',
    account: 'http://localhost:4008',
    video: 'http://localhost:4009',
    'ptv-appshell': 'http://localhost:4010',
};

/**
 *
 * @param {string} affectedAppString
 * @returns {string[]}
 */
export const cleanAffectedAppString = (affectedAppString) => {
    const array = affectedAppString.split(',');
    return array.map((path) => path.split('/')[1]);
};

/**
 *
 * @param {MfeNames} name
 * @returns {string}
 */
export const cleanMfeName = (name, removeAppshellPrefix = true) => {
    const isAppShell = name.match(/app-?shell$/);

    // app-shell needs to be rewritten to appshell
    // Remove ptv- prefix from ptv-appshell
    if (removeAppshellPrefix && isAppShell) {
        return 'appshell';
    }

    // chromecast-receiver needs to keep the dash
    // to match the deployed domain name
    // ptv-appshell needs to keep the dash if removeAppshellPrefix is false (platypus)
    if (name.includes('chromecast') || name.includes('ptv-appshell')) {
        return name;
    }
    return name.replace('-', '');
};

/**
 *
 * @param {string} namespace
 * @returns { [string|null, string, string] }
 */
export const parseNamespace = (namespace) => {
    if (namespace.includes('-')) {
        const [_, location, ...envStringArr] = namespace.split('-');
        const env = envStringArr.join('-');
        return [_, location, env];
    }

    return [null, 'us', 'platypus'];
};

/**
 * Returns the localhost URL for a given MFE name.
 *
 * When `lob` is `'ptv'` and `name` is `'app-shell'`, the Pluto TV app-shell
 * (`ptv-appshell`) is returned instead, so local dev always resolves to the
 * correct host regardless of which app-shell key is requested.
 *
 * @param {MfeNames} name - The MFE name to look up (e.g. `'app-shell'`, `'hubs'`).
 * @param {string} [lob='pplus'] - Line of business (`'pplus'` or `'ptv'`).
 * @param {boolean} isServer
 * @returns {typeof mfePaths[MfeNames]} The localhost origin for the MFE.
 */
const getLocalDomain = (name, lob = 'pplus', isServer = false) => {
    if (isServer && localHttpsEnabled && mfeHttpPaths[name]) {
        if (lob === 'ptv' && name === 'app-shell') {
            return mfeHttpPaths['ptv-appshell'];
        }
        return mfeHttpPaths[name];
    }
    if (lob === 'ptv' && name === 'app-shell') {
        return mfePaths['ptv-appshell'];
    }
    return mfePaths[name];
};

/**
 *
 * @param {MfeNames} name
 * @param {string} branch
 * @returns {string}
 */
const getDynamicFeatureInstanceDomain = (name, branch, lob = 'pplus') => {
    const domain = getDomainByLOB(lob);
    const canonicalName = getCanonicalAppName(name, lob);
    const isAppShell = canonicalName.match(/app-?shell$/);
    let mfeName = cleanMfeName(canonicalName, false);

    if (isAppShell && lob === 'ptv') {
        mfeName = 'ptv-appshell';
    }

    return `https://web-mf-${mfeName}-${branch}.shared.branch.${domain}`;
};

/**
 * HTTPS origin for a Platypus feature-instance host (shared.branch), matching buildRemotes / getDomain.
 * @param {keyof typeof mfePaths | string} mfeName
 * @param {string} branch - SUBDOMAIN / Jira slug (e.g. cbsentweb-4727)
 * @param {string} [lob='pplus']
 * @returns {string}
 */
export const getFeatureInstanceOrigin = (mfeName, branch, lob = 'pplus') => {
    return getDynamicFeatureInstanceDomain(
        /** @type {keyof typeof mfePaths} */ (mfeName),
        branch,
        lob,
    );
};

/**
 * Parses `web-mf-{mfe}[-{branch}].shared.branch.{apex}` using one regex and the same known-MFE slug list as
 * `parsePlatypusSubdomainFromHostname` / `getSharedBranchCookieDomain`.
 * Returns null if the shape matches loosely but `{mfe}` is not a recognized `mfePaths` slug.
 * @param {string} hostname
 * @returns {{ apexDomain: string, branch: string } | null}
 */
const parsePlatypusFeatureInstanceHostParts = (hostname) => {
    if (!hostname || typeof hostname !== 'string') {
        return null;
    }
    const host = hostname.split(':')[0];
    const match = host.match(/^web-mf-(.+)\.shared\.branch\.(paramountplus\.com|pluto\.tv)$/i);
    if (!match) {
        return null;
    }
    const rest = match[1];
    const apexDomain = match[2].toLowerCase();
    const slugCandidates = Object.keys(mfePaths)
        .map((key) => ({
            key,
            slug: cleanMfeName(/** @type {keyof typeof mfePaths} */ (key), false),
        }))
        .sort((a, b) => b.slug.length - a.slug.length);
    for (const { slug } of slugCandidates) {
        if (rest === slug) {
            return { apexDomain, branch: '' };
        }
        if (rest.startsWith(`${slug}-`)) {
            return { apexDomain, branch: rest.slice(slug.length + 1) };
        }
    }
    return null;
};

/**
 * Parse SUBDOMAIN from a feature-instance hostname (web-mf-{mfe}-{branch}.shared.branch.{domain}).
 * Returns '' if the host is not FI-shaped or no branch segment is found.
 * @param {string} hostname
 * @returns {string}
 */
export const parsePlatypusSubdomainFromHostname = (hostname) => {
    const parts = parsePlatypusFeatureInstanceHostParts(hostname);
    return parts ? parts.branch : '';
};

/**
 * Cookie `Domain` for dev tooling on platypus feature-instance hosts so cookies are sent across
 * every `web-mf-*` MFE subdomain on the same branch (e.g. appshell vs signup).
 * Uses the same parser as `parsePlatypusSubdomainFromHostname` (known MFE slugs only).
 *
 * @param {string} hostname e.g. `window.location.hostname`
 * @returns {string} e.g. `.shared.branch.paramountplus.com`, or `''` if not FI-shaped
 */
export const getSharedBranchCookieDomain = (hostname) => {
    const parts = parsePlatypusFeatureInstanceHostParts(hostname);
    return parts ? `.shared.branch.${parts.apexDomain}` : '';
};

/**
 * Browser/runtime deployment env aligned with `dynamic-remote-plugin` client bootstrap, plus a
 * `process.env.CURRENT_ENV` fallback for Node scripts and Jest when window globals are unset.
 *
 * Order: `window.__CURRENT_ENV__` → `window.__NEXT_DATA__.props.currentEnv` → `process.env.CURRENT_ENV` → `'prod'`.
 *
 * @returns {string}
 */
export const getClientRuntimeCurrentEnv = () => {
    if (typeof window === 'undefined') {
        return process.env.CURRENT_ENV || 'prod';
    }
    /** @type {Window & { __CURRENT_ENV__?: string; __NEXT_DATA__?: { props?: { currentEnv?: string } } }} */
    const w = window;
    if (w.__CURRENT_ENV__) {
        return w.__CURRENT_ENV__;
    }
    const fromNextData = w.__NEXT_DATA__?.props?.currentEnv;
    if (fromNextData) {
        return fromNextData;
    }
    return process.env.CURRENT_ENV || 'prod';
};

/**
 * Platypus feature-instance branch (`SUBDOMAIN`) on the client: prefer hostname-derived slug, else `process.env`.
 *
 * @returns {string}
 */
const getClientRuntimeBranch = () => {
    if (typeof window === 'undefined') {
        return process.env.SUBDOMAIN ?? '';
    }
    try {
        const hostname = window.location.hostname.split(':')[0];
        const fromHost = parsePlatypusSubdomainFromHostname(hostname);
        if (fromHost) {
            return fromHost;
        }
    } catch {
        // ignore invalid location in non-browser test doubles
    }
    return process.env.SUBDOMAIN ?? '';
};

/**
 * LOB hint when resolving public URLs in the browser (Pluto host → `ptv`).
 *
 * @param {boolean} isServer
 * @returns {string}
 */
const getLobForDomainResolution = (isServer) => {
    if (isServer || typeof window === 'undefined') {
        return getLOB();
    }
    try {
        const hostname = window.location.hostname.toLowerCase();
        if (hostname.includes('pluto.tv')) {
            return 'ptv';
        }
    } catch {
        // ignore
    }
    return getLOB();
};

/**
 *
 * @param {MfeNames} name
 * @param {string} location
 * @param {string} env
 * @param {string} lob - Line of business. Valid values are 'pplus' (Paramount+) or 'ptv' (Pluto TV).
 * @param {boolean} isServer
 * @returns {string}
 */
const getDynamicMFEDomain = (name, location, env, lob = 'pplus', isServer = false) => {
    const appConst = env === 'prod' ? env : 'dev';
    const envConst = env === 'platypus' ? 'stage' : env;
    const lobConst = lob === 'ptv' ? 'pluto' : 'pplus';
    const subdomain = lob === 'ptv' ? 'gcp' : 'app';
    const domain = getDomainByLOB(lob);
    // Remove prefix from ptv-appshell
    const mfeName = cleanMfeName(getCanonicalAppName(name, lob));

    if (!isServer) {
        return `https://www-${mfeName}-${lobConst}-${location}-${envConst}.${subdomain}-${appConst}-usc1.${domain}`;
    }

    return `${getDynamicKubeDNSDomain(name)}.${lobConst}-${location}-${envConst}`;
};

/**
 *
 * @param {string} env
 * @param {string} lob The line of business. Valid values are 'pplus' or 'ptv'.
 * @returns {string}
 */
export const getDynamicLegacyDomain = (env, lob = 'pplus') => {
    const domain = getDomainByLOB(lob);
    if (env === 'prod') {
        // PPlus prod uses www; Pluto TV prod uses bare domain (pluto.tv, not www.pluto.tv)
        const host = lob === 'ptv' ? domain : `www.${domain}`;
        return `https://${host}`;
    }
    return `https://${env}.${domain}`;
};

/**
 *
 * @param {MfeNames} name
 * @returns {string}
 */
const getDynamicKubeDNSDomain = (name) => {
    return `http://www-${cleanMfeName(name)}`;
};

/**
 * Resolve domain for platypus environment (feature branches or stage MFE).
 * When SUBDOMAIN is set, always use the feature-instance (shared.branch) URL for every MFE
 * so routing matches FI deploys without maintaining AFFECTED_APPS.
 * When SUBDOMAIN is empty, fall back to stage / KubeDNS (local jobs, etc.).
 * @param {object} opts
 * @param {import('./domains.js').MfeNames} opts.appName
 * @param {string} opts.branch - SUBDOMAIN / Jira key for FI hostnames
 * @param {string} opts.lob
 * @param {string} opts.locationOverride
 * @param {string} opts.location
 * @param {boolean} opts.isServer
 * @returns {string}
 */
const resolvePlatypusDomain = ({ appName, branch, lob, locationOverride, location, isServer }) => {
    if (branch?.trim()) {
        return getDynamicFeatureInstanceDomain(appName, branch, lob);
    }
    const configEnv = process.env.CONFIG_ENV;
    const mfeEnv = !configEnv || configEnv === 'platypus' ? 'stage' : configEnv;
    const loc = locationOverride || location;
    return getDynamicMFEDomain(appName, loc, mfeEnv, lob, isServer);
};

/**
 * Jenkins multiregion deploy labels: `{lob}-{location}-{env}` (e.g. `pplus-us-stage`).
 * Distinct from `NAMESPACE=platypus` (feature-instance) and from empty `NAMESPACE` (reusable image).
 * @const
 */
const MULTIREGION_NAMESPACE_PATTERN = /^(pplus|ptv)-/i;

/**
 * Whether this build was deployed via the multiregion Jenkins pipeline with an explicit
 * `NAMESPACE` like `pplus-us-stage` or `ptv-intl-preview`.
 *
 * Those deploys expose each MFE on its own public host:
 * - P+: `www-{mfe}-pplus-{location}-{env}.app-dev-usc1.paramountplus.com`
 * - PTV: `www-{mfe}-pluto-{location}-{env}.gcp-dev-usc1.pluto.tv`
 * Reusable/promoted images leave `NAMESPACE` empty and must keep legacy apex URLs
 * (`stage.paramountplus.com`) so runtime remotes can be rewritten.
 *
 * @param {string} namespace `process.env.NAMESPACE` at build time
 * @returns {boolean}
 */
const usesPerMfePublicHosts = (namespace) =>
    Boolean(namespace && MULTIREGION_NAMESPACE_PATTERN.test(namespace));

/**
 * Resolve domain for higher environments (stage, prod, preview, etc.)
 *
 * Client routing:
 * - Multiregion `NAMESPACE` + non-prod → per-MFE public host ({@link getDynamicMFEDomain})
 * - Reusable image (empty `NAMESPACE`) or prod → legacy apex ({@link getDynamicLegacyDomain})
 *
 * @param {import('./domains.js').MfeNames} appName
 * @param {string} environment Parsed env from `NAMESPACE` (e.g. `stage`, `prod`)
 * @param {string} lob
 * @param {boolean} isServer
 * @param {string} locationOverride
 * @param {string} location
 * @param {string} namespace
 * @returns {string}
 */
const resolveHigherEnvironmentDomain = (
    appName,
    environment,
    lob,
    isServer,
    locationOverride,
    location,
    namespace,
) => {
    if (isServer) {
        return getDynamicKubeDNSDomain(appName);
    }
    const routeToPerMfeHost = environment !== 'prod' && usesPerMfePublicHosts(namespace);
    if (routeToPerMfeHost) {
        const loc = locationOverride || location;
        return getDynamicMFEDomain(appName, loc, environment, lob, false);
    }
    return getDynamicLegacyDomain(environment, lob);
};

/**
 * Gets the appropriate domain URL for a micro-frontend (MFE) based on the current environment and configuration.
 *
 * See {@link ./GET_DOMAIN.md} for the full parameter/env matrix and URL templates.
 *
 * This function determines the domain to use for module federation remotes and API calls. The domain returned
 * depends on several factors:
 * - Environment type (local, platypus, stage, prod, etc.)
 * - For platypus: SUBDOMAIN set → feature-instance URL for all MFEs; otherwise stage / KubeDNS
 * - Line of Business (LOB): 'pplus' (Paramount+) or 'ptv' (Pluto TV)
 * - Whether the request is server-side or client-side
 * - Location (us, intl, etc.)
 *
 * **Note**: On the server and in Node, defaults to production URLs when `CURRENT_ENV` is not set (reusable Docker images).
 * In the browser (`isServer === false`), prefers `window.__CURRENT_ENV__` / `__NEXT_DATA__.props.currentEnv` before
 * `process.env`, so runtime promotion matches federation behavior; the dynamic-remote-plugin still rewrites remote entries.
 *
 * @param {MfeNames|null} name - The name of the micro-frontend app (e.g., 'app-shell', 'hubs', 'account').
 * @param {string} locationOverride - Optional location override (e.g., 'us', 'intl').
 * @param {boolean} isServer - Whether this is a server-side request (true) or client-side request (false).
 *
 * @returns {string} The domain URL for the specified micro-frontend. Examples:
 *   - Local: 'http://localhost:3000'
 *   - Platypus feature instance: 'https://web-mf-appshell-feature-1234.shared.branch.paramountplus.com'
 *   - Platypus stage (client): 'https://www-appshell-pplus-us-stage.app-dev-usc1.paramountplus.com'
 *   - Platypus stage (server): 'http://www-appshell.pplus-us-stage'
 *   - Production (client): 'https://www.paramountplus.com' (pplus) or 'https://pluto.tv' (ptv)
 *   - Production (server): 'http://www-appshell'
 */
export const getDomain = (name = null, locationOverride = '', isServer = false) => {
    const useClientRuntime = !isServer && typeof window !== 'undefined';

    // Default to 'prod' for reusable Docker images - plugin will transform for other environments
    const currentEnv = useClientRuntime
        ? getClientRuntimeCurrentEnv()
        : process.env.CURRENT_ENV || 'prod';
    const namespace = process.env.NAMESPACE ?? '';
    const branch = useClientRuntime ? getClientRuntimeBranch() : (process.env.SUBDOMAIN ?? '');
    const [_, location, environment] = parseNamespace(namespace);
    // If no namespace at build time, default environment to currentEnv (for reusable Docker images)
    // parseNamespace('') returns 'platypus', but we want to use currentEnv instead
    const resolvedEnvironment = !namespace ? currentEnv : environment;
    const lob = getLobForDomainResolution(isServer);
    const defaultAppName = lob === 'pplus' ? 'app-shell' : 'ptv-appshell';
    const appName = name === 'user-profiles' ? defaultAppName : (name ?? defaultAppName);

    // **Local environment**: Returns localhost URL with port (e.g., 'http://localhost:3000')
    if (currentEnv === 'local') {
        return getLocalDomain(appName, lob, isServer);
    }
    // **Platypus environment** (feature branches):
    // - If SUBDOMAIN is set: feature instance domain for every MFE (e.g. web-mf-appshell-{SUBDOMAIN}.shared.branch…)
    // - If SUBDOMAIN is empty: stage (client) or KubeDNS (server)
    if (resolvedEnvironment === 'platypus') {
        return resolvePlatypusDomain({
            appName,
            branch,
            lob,
            locationOverride,
            location,
            isServer,
        });
    }
    // **Higher environments** (stage, prod, preview, etc.)
    // - Server-side: KubeDNS (e.g. 'http://www-appshell')
    // - Client-side: per-MFE public hosts when NAMESPACE is a Jenkins multiregion label (pplus-us-stage);
    //   legacy apex (stage.paramountplus.com) when NAMESPACE is empty (reusable image) or prod
    return resolveHigherEnvironmentDomain(
        appName,
        resolvedEnvironment,
        lob,
        isServer,
        locationOverride,
        location,
        namespace,
    );
};
